import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Divider, Icon } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';

import Loader from '../../common/loader';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get, post } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    divider: {
        marginBottom: 24
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 20,
        marginBottom: 20
    },
    cancel: {
        ...themeColors.grayBtn
    },
    store: {
        marginLeft: 20,
        ...themeColors.blueBtn
    },
    tableContainer: {
        width: '100%'
    }
}));

const GroupCreate = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const history = useHistory();
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedPolicies, setSelectedPolicies] = useState([]);
    const [state, setState] = useState({
        loading: true,
        form: {
            name: '',
            description: ''
        },
        users: [],
        policies: [],
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Create user group | IAM';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/iamGroup/create');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                users: response.users,
                policies: response.policies
            }));
        } else {
            console.log('[GroupCreate] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, form: { ...prev.form, [target.name]: target.value } }));

    const validateStore = () => {
        const { name } = state.form;
        const regex = /^[a-zA-Z0-9+=,.@-_]+$/;

        if (!name.trim()) {
            showToast('Name field is required', 'error');
            return false;
        }
        if (!regex.test(name)) {
            showToast(`Name field: use alphanumeric and '+=,.@-' characters`, 'error', 6000);
            return false;
        }

        return true;
    }

    const store = async () => {
        const cond = validateStore();
        if (cond) {
            showLoader(true);
            const response = await post('/iamGroup/store', { ...state.form, selectedUsers, selectedPolicies });
            if (response.status === 200) {
                showToast(response.message);
                history.push('/iam/groups');
            } else {
                console.log('[GroupCreate] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const getGroupsNumber = ({ value }) => value?.length ?? '0';

    const getPolicyType = ({ value }) => (value) ? 'CFG managed' : 'User managed';
    const renderName = ({ value, row }) => {
        return (
            <>
                {
                    row?.default &&
                    <Icon fontSize='inherit' style={{ marginRight: 8 }}>{'key_vertical'}</Icon>
                }
                <span>{` ${value}`}</span>
            </>
        );
    }

    const userColumns = [
        { field: 'username', headerName: 'Username', flex: 1, minWidth: 200 },
        { field: 'names', headerName: 'Names', flex: 1, minWidth: 200 },
        { field: 'surnames', headerName: 'Surnames', flex: 1, minWidth: 200 },
        { field: 'groups', headerName: 'Groups', flex: 1, minWidth: 200, valueGetter: getGroupsNumber }
    ];

    const policyColumns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
        { field: 'default', headerName: 'Type', flex: 1, minWidth: 200, valueGetter: getPolicyType, renderCell: renderName  },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 }
    ];

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <h2>{'Create user group'}</h2>

            <Grid container>
                <Grid item xs={12} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Name'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Name'
                                name='name'
                                value={state.form.name}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={9}>
                    <FormGroup>
                        <FormLabel>{'Description'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Description'
                                name='description'
                                value={state.form.description}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            <div className={classes.tableContainer}>
                <h2>{'Add users to the group'}</h2>
                <DataGrid
                    rows={state.users}
                    columns={userColumns}
                    pageSize={10}
                    density='compact'
                    autoHeight
                    getRowId={row => row.iduser}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={array => setSelectedUsers(array)}
                />
            </div>

            <div className={classes.tableContainer}>
                <h2>{'Attach policies'}</h2>
                <DataGrid
                    rows={state.policies}
                    columns={policyColumns}
                    pageSize={20}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idiam_policy}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={array => setSelectedPolicies(array)}
                />
            </div>

            <div className={classes.controls}>
                <Button
                    variant='contained'
                    className={classes.cancel}
                    component={Link}
                    to={'/iam/groups'}
                >
                    {'Cancel'}
                </Button>

                <Button
                    variant='contained'
                    className={classes.store}
                    onClick={store}
                >
                    {'Store'}
                </Button>

            </div>
        </div>
    );
}

export default GroupCreate;